import {Component, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {GoldenRecordService} from "../../../Services/goldenRecord.service";
import {LoginService} from "../../../Services/login.service";
import {GoldenRecord} from "../../../Model/goldenRecord";
import {CatalogService} from "../../../Services/CatalogManagement/catalog.service";
import {BackupService} from "../../../Services/CatalogManagement/backup.service";
import {Backup} from "../../../Model/System/Backup";
import {DxContextMenuComponent} from "devextreme-angular";
import {TranslateService} from "@ngx-translate/core";
import {SystemService} from "../../../Services/system.service";
import {custom} from "devextreme/ui/dialog";
import {lastValueFrom} from "rxjs";
import {GoldenRecordRow} from "./golden-record-row.model";

@Component({
    selector: 'app-golden-record-stage',
    templateUrl: './golden-record-stage.component.html',
    styleUrls: ['./golden-record-stage.component.css'],
    standalone: false
})
export class GoldenRecordStageComponent implements OnInit {

  @ViewChild('rowMenu', {static: false})
  rowMenu?: DxContextMenuComponent;

  public goldenRecord?: GoldenRecordRow;
  public backups: Backup[] = [];
  public selectedCatalogId: string = "";

  private menuContext?: { type: 'goldenRecord', row: GoldenRecordRow } | { type: 'backup', backup: Backup };

  public currentMenuItems: any[] = [];

  private readonly goldenRecordMenuItems = [
    {id: 'delete', text: this.translate.instant('Löschen')},
  ];

  private readonly backupMenuItems = [
    {id: 'promote', text: this.translate.instant('Zum Golden Record promoten')},
    {id: 'delete', text: this.translate.instant('Löschen')},
  ];

  constructor(
    private readonly router: Router,
    private readonly goldenRecordService: GoldenRecordService,
    private readonly catalogService: CatalogService,
    private readonly backupService: BackupService,
    private readonly loginService: LoginService,
    private readonly translate: TranslateService,
    private readonly systemService: SystemService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadGoldenRecord();
  }

  private async loadGoldenRecord() {
    const customerId = this.loginService.currentCustomer.id;
    try {
      const gr = await this.goldenRecordService.getGoldenRecord(customerId);
      this.goldenRecord = {
        id: gr.id,
        catalogId: gr.catalogId,
        catalogName: await this.catalogService.getCatalogName(gr.catalogId, customerId),
        createdAt: gr.createdAt
      };
      this.selectedCatalogId = gr.catalogId;
      await this.loadBackups(customerId, gr.catalogId, gr.previousCatalogId);
    } catch {
      this.goldenRecord = undefined;
      this.backups = [];
    }
  }

  private async loadBackups(customerId: string, catalogId: string, previousCatalogId?: string) {
    try {
      let backups = await lastValueFrom(this.backupService.getBackupsByCatalog(customerId, catalogId));

      if (previousCatalogId && previousCatalogId !== catalogId) {
        try {
          const previousBackups = await lastValueFrom(this.backupService.getBackupsByCatalog(customerId, previousCatalogId));
          backups = [...backups, ...previousBackups];
        } catch { /* keine backups */ }
      }

      const uniqueBackups = new Map(backups.map(b => [b.id, b]));
      this.backups = Array.from(uniqueBackups.values()).sort((a, b) =>
        new Date(b.backUpTimeStamp!).getTime() - new Date(a.backUpTimeStamp!).getTime()
      );
    } catch {
      this.backups = [];
    }
  }

  onNameClick(row: GoldenRecordRow) {
    const customerId = this.loginService.currentCustomer.id;
    this.router.navigate([
      '/dataprocess/golden-record/catalog',
      customerId,
      row.catalogId,
      'infos'
    ]);
  }

  openGoldenRecordMenu(e: any, row: GoldenRecordRow) {
    this.menuContext = {type: 'goldenRecord', row};
    this.currentMenuItems = this.goldenRecordMenuItems;
    this.showMenu(e);
  }

  openBackupMenu(e: any, backup: Backup) {
    this.menuContext = {type: 'backup', backup};
    this.currentMenuItems = this.backupMenuItems;
    this.showMenu(e);
  }

  private showMenu(e: any) {
    const targetElement = e.element as HTMLElement;
    this.rowMenu?.instance.option('target', targetElement);
    this.rowMenu?.instance.option('dataSource', this.currentMenuItems);
    this.rowMenu?.instance.show();
  }

  async onMenuItemClick(event: any) {
    if (!this.menuContext)
      return;

    const menuItemId = event.itemData.id;

    if (menuItemId === 'delete') {
      if (this.menuContext.type === 'goldenRecord') {
        await this.onDeleteGoldenRecord(this.menuContext.row);
      } else {
        await this.onDeleteBackup(this.menuContext.backup);
      }
    }

    if (menuItemId === 'promote' && this.menuContext.type === 'backup') {
      await this.onPromoteBackup(this.menuContext.backup);
    }
  }

  private async onPromoteBackup(backup: Backup) {
    const confirmDialog = custom({
      title: this.translate.instant('Backup promoten?'),
      messageHtml: `${this.translate.instant('Soll dieses Backup zum Golden Record promoted werden?')}<br/><br/><b>${backup.catalogName}</b><br/><br/>${this.translate.instant('Der aktuelle Golden Record wird automatisch als Backup gesichert.')}`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    const customerId = this.loginService.currentCustomer.id;

    try {
      await this.goldenRecordService.promoteBackup(backup.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Backup wird zum Golden Record promoted. Bitte warten Sie auf die Fertigstellung der Hintergrund-Jobs.'));
      await this.loadGoldenRecord();
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Promoten des Backups'));
    }
  }

  private async onDeleteGoldenRecord(row: GoldenRecordRow) {
    const confirmDialog = custom({
      title: this.translate.instant('Wirklich löschen?'),
      messageHtml: `${this.translate.instant('Wollen Sie diesen Golden Record wirklich löschen?')}<br/><br/><b>${row.catalogName}</b>`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    const customerId = this.loginService.currentCustomer.id;
    try {
      await this.goldenRecordService.delete(row.id, customerId);
      this.systemService.notifyInfo(this.translate.instant('Golden Record erfolgreich gelöscht'));
      await this.loadGoldenRecord();
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Löschen des Golden Records'));
    }
  }

  private async onDeleteBackup(backup: Backup) {
    const confirmDialog = custom({
      title: this.translate.instant('Wirklich löschen?'),
      messageHtml: `${this.translate.instant('Wollen Sie dieses Backup wirklich löschen?')}<br/><br/><b>${backup.catalogName}</b>`,
      buttons: [
        {text: this.translate.instant('Ja'), onClick: () => true},
        {text: this.translate.instant('Nein'), onClick: () => false}
      ]
    });

    if (!await confirmDialog.show())
      return;

    try {
      this.backupService.deleteBackup(backup.id);
      this.systemService.notifyInfo(this.translate.instant('Backup erfolgreich gelöscht'));
      const customerId = this.loginService.currentCustomer.id;
      if (this.goldenRecord) {
        await this.loadBackups(customerId, this.goldenRecord.catalogId);
      }
    } catch {
      this.systemService.notifyInfo(this.translate.instant('Fehler beim Löschen des Backups'));
    }
  }
}
